/**
 * ProUpgradeModal — non-blocking upsell shown after a successful PDF
 * publish to nudge standalone-mode users toward Solar Proof Pro Tier 1.
 *
 * The standalone SLD tool is a thin slice of what the full Solar Proof
 * platform offers: in Pro the diagram is auto-generated from the real
 * project data (inverters/panels/batteries selected during the quote),
 * gets cloud-saved + shareable, picks up branded title blocks, and
 * sits inside a much bigger pipeline (cover art, financials, NETCC
 * compliance, T&Cs, lead capture, signup flow, etc).
 *
 * Behaviour:
 *  - Shown automatically after PDF export completes successfully
 *  - "Don't show again" stores a flag in localStorage so we don't
 *    badger anyone who's already declined
 *  - Esc / outside-click / "Maybe later" all just dismiss
 *  - Primary CTA opens the pricing page in a new tab
 */
import { useEffect } from 'react';

const PRICING_URL = 'https://www.solarproof.com.au/pricing_tiers';
const SUPPRESS_KEY = 'sld-pro-modal-suppressed';

export function isProUpgradeSuppressed(): boolean {
  try {
    return localStorage.getItem(SUPPRESS_KEY) === '1';
  } catch {
    return false;
  }
}

export function suppressProUpgrade(): void {
  try {
    localStorage.setItem(SUPPRESS_KEY, '1');
  } catch {
    /* private mode etc — modal will simply re-show next time */
  }
}

interface Props {
  open: boolean;
  onClose: () => void;
}

interface Feature {
  icon: string;
  title: string;
  body: string;
}

// The headline value props the standalone SLD tool can't deliver but
// the full SP platform can. Ordered roughly by "what would make ME
// upgrade right now" — automation + cloud first, then branding, then
// the bigger pipeline picture.
const FEATURES: Feature[] = [
  {
    icon: '⚡',
    title: 'Auto-generate from project data',
    body: 'The SLD draws itself from the inverters, panels and batteries you already picked for the quote — no rebuild required.',
  },
  {
    icon: '☁',
    title: 'Cloud save & share',
    body: 'Every diagram, quote and customer file lives in one account. Open from any device, share a link with your installer.',
  },
  {
    icon: '🎨',
    title: 'Branded title blocks & exports',
    body: 'Your logo, your colours, your fonts — applied automatically to every SLD, cover, page header and footer.',
  },
  {
    icon: '📄',
    title: 'Full quote pipeline',
    body: 'Cover, financial analysis, system design, site plan, energy profile, NETCC clauses, terms & conditions — generated end-to-end.',
  },
  {
    icon: '🚀',
    title: 'And the rest of Solar Proof',
    body: 'Lead capture & signup flows, AI brand extraction, retailer integrations, payback charts, customer dashboards.',
  },
];

export function ProUpgradeModal({ open, onClose }: Props) {
  // Esc dismisses. Mirrors ConfirmDialog's behaviour so all modals
  // feel the same.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const handleDontShowAgain = () => {
    suppressProUpgrade();
    onClose();
  };

  const handleUpgrade = () => {
    window.open(PRICING_URL, '_blank', 'noopener,noreferrer');
    // Don't auto-close on click — let the user decide. They might
    // want to keep the modal as a reference. They can dismiss
    // explicitly. Actually scrap that — the upgrade tab opens, this
    // tab no longer needs the modal in their face.
    onClose();
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="pro-upgrade-title"
      onClick={(e) => {
        // Click on the overlay (not the card) dismisses.
        if (e.target === e.currentTarget) onClose();
      }}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15, 23, 42, 0.55)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        padding: 'var(--space-4)',
        backdropFilter: 'blur(2px)',
      }}
    >
      <div
        style={{
          background: 'var(--surface)',
          borderRadius: 'var(--radius-lg)',
          boxShadow: 'var(--shadow-modal)',
          maxWidth: 520,
          width: '100%',
          maxHeight: '90vh',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          border: '1px solid var(--border)',
        }}
      >
        {/* Header band — orange→blue gradient that matches Solar
            Proof's brand without bringing in marketing chrome. */}
        <div
          style={{
            background: 'linear-gradient(135deg, #f97316 0%, #2563eb 100%)',
            color: '#ffffff',
            padding: '20px 24px',
            position: 'relative',
          }}
        >
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1.2, opacity: 0.9, textTransform: 'uppercase' }}>
            Solar Proof Pro · Tier 1
          </div>
          <h2
            id="pro-upgrade-title"
            style={{
              margin: '4px 0 0 0',
              fontSize: 22,
              fontWeight: 700,
              lineHeight: 1.25,
            }}
          >
            Nice work — your PDF is ready.
          </h2>
          <div style={{ marginTop: 6, fontSize: 13, opacity: 0.92, lineHeight: 1.5 }}>
            See what else Solar Proof can do for your quotes.
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            style={{
              position: 'absolute',
              top: 12,
              right: 12,
              width: 28,
              height: 28,
              border: 'none',
              borderRadius: '50%',
              background: 'rgba(255, 255, 255, 0.2)',
              color: '#ffffff',
              cursor: 'pointer',
              fontSize: 16,
              lineHeight: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            ×
          </button>
        </div>

        {/* Feature list */}
        <div
          style={{
            padding: '18px 24px 8px 24px',
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: 14,
          }}
        >
          {FEATURES.map((f) => (
            <div
              key={f.title}
              style={{
                display: 'flex',
                gap: 12,
                alignItems: 'flex-start',
              }}
            >
              <div
                aria-hidden
                style={{
                  flexShrink: 0,
                  width: 32,
                  height: 32,
                  borderRadius: 8,
                  background: 'var(--surface-2)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 16,
                }}
              >
                {f.icon}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--text)' }}>{f.title}</div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.5, marginTop: 2 }}>
                  {f.body}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Footer actions */}
        <div
          style={{
            padding: '16px 24px 18px 24px',
            borderTop: '1px solid var(--border)',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            flexWrap: 'wrap',
          }}
        >
          <button
            type="button"
            onClick={handleDontShowAgain}
            className="btn-tertiary"
            style={{ fontSize: 11, color: 'var(--text-faint)' }}
          >
            Don't show again
          </button>
          <div style={{ flex: 1 }} />
          <button type="button" onClick={onClose} className="btn-tertiary">
            Maybe later
          </button>
          <button
            type="button"
            onClick={handleUpgrade}
            className="btn-primary"
            style={{ fontWeight: 600 }}
          >
            See Pro plans →
          </button>
        </div>
      </div>
    </div>
  );
}
